import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clapperboard, Play, X } from "lucide-react";
import { getFolder, removeFromFolder } from "../lib/api";
import { formatDate, PLATFORM_LABELS } from "../lib/youtube";

export default function FolderDetail() {
  const { folderId } = useParams();
  const [folder, setFolder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getFolder(folderId).then(setFolder).catch(() => {});
  }, [folderId]);

  const unsave = async (e, videoId) => {
    e.stopPropagation();
    await removeFromFolder(folderId, videoId).catch(() => {});
    setFolder((f) => ({ ...f, videos: f.videos.filter((v) => v.video_id !== videoId) }));
  };

  if (!folder) {
    return (
      <div className="py-32 text-center">
        <span className="font-meta text-xs uppercase tracking-[0.3em] text-zinc-600">Loading folder…</span>
      </div>
    );
  }

  return (
    <div data-testid="folder-detail-page" className="max-w-5xl mx-auto py-16 px-6 w-full">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <Link to="/saves" data-testid="back-to-saves" className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 hover:text-white transition-colors duration-200">
          ← Saves
        </Link>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mt-4 mb-4">{folder.name}</h1>
        <p className="font-meta text-xs text-zinc-500 mb-12" data-testid="folder-count">
          {folder.videos.length} {folder.videos.length === 1 ? "FILM" : "FILMS"} SAVED
        </p>
      </motion.div>

      {folder.videos.length === 0 && (
        <div data-testid="folder-empty" className="py-24 text-center border border-dashed border-white/10 rounded-2xl">
          <p className="font-heading text-2xl text-zinc-600">Nothing saved here yet.</p>
          <p className="mt-3 font-meta text-xs uppercase tracking-[0.25em] text-zinc-700">Use the bookmark button while a video plays</p>
        </div>
      )}

      <div className="flex flex-col">
        {folder.videos.map((video, i) => (
          <motion.div
            key={video.video_id}
            data-testid={`saved-item-${video.video_id}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => navigate(`/?v=${video.video_id}`)}
            className="group grid grid-cols-1 md:grid-cols-12 gap-6 p-4 -mx-4 rounded-2xl hover:bg-white/[0.02] transition-colors duration-300 border-b border-white/5 last:border-none items-center cursor-pointer"
          >
            <div className="col-span-1 md:col-span-3 aspect-video rounded-lg overflow-hidden bg-zinc-900 relative">
              {video.thumbnail ? (
                <img src={video.thumbnail} alt={video.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <Clapperboard className="w-8 h-8" strokeWidth={1} />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                <Play className="w-8 h-8 text-white fill-white" />
              </div>
            </div>
            <div className="col-span-1 md:col-span-8 flex flex-col gap-2">
              <h2 className="text-xl md:text-2xl font-heading text-white line-clamp-2">{video.title}</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-meta text-xs text-zinc-500 tracking-widest">{formatDate(video.added_at)}</span>
                <span className="font-meta text-[10px] tracking-[0.2em] text-zinc-600 border border-white/10 rounded-full px-2.5 py-0.5">
                  {PLATFORM_LABELS[video.platform] || "VIDEO"}
                </span>
              </div>
            </div>
            <div className="col-span-1 md:col-span-1 flex justify-end md:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button
                data-testid={`unsave-video-${video.video_id}`}
                onClick={(e) => unsave(e, video.video_id)}
                aria-label="Remove from folder"
                className="p-3 rounded-full text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors duration-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
